import {useAuth} from '@apps-common/auth-react';
import {Box, Button, SvgIcon, Typography, styled} from '@mui/material';
import {Map, TileLayer, VectorLayer, VectorTileLayer} from '@planet/layers';
import chroma from 'chroma-js';
import Feature from 'ol/Feature';
import {MousePosition} from 'ol/control';
import {buffer, getCenter, getWidth} from 'ol/extent';
import Point from 'ol/geom/Point';
import {fromExtent} from 'ol/geom/Polygon';
import HeatMapLayer from 'ol/layer/Heatmap';
import {transformExtent} from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import {Circle, Fill, Icon, Stroke, Style} from 'ol/style';
import PropTypes from 'prop-types';
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {getVectorTilesURL} from '../api.js';
import ViewChangeIcon from '../assets/ViewChange.jsx';
import {MAPBOX_DARK} from '../config.js';
import {MODE, TIME_MODE} from '../constants/heatmap.js';
import {
  useClipCallbacks,
  useMercatorGeometry,
  useZoomCallback,
} from '../hooks/map.js';
import {usePopup} from '../hooks/popup.jsx';
import {mapBackground} from '../theme.js';
import {getImageryURL} from '../util/collection.js';
import {getClassColorLookup} from '../util/feed.js';
import {
  formatCoords,
  getExtentForGeometry,
  getFeatureFromEvent,
  scaleExtent,
} from '../util/map.js';
import {
  clipStyle,
  fill,
  getDrawStyle,
  heatMapGradient,
  scale,
  selectStyle,
} from '../util/styles.js';
import ChangeViewer from './ChangeViewer.jsx';
import MapToolbar from './MapToolbar.jsx';
import MapTools from './MapTools.jsx';

const RootDiv = styled('div')({
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
});

const Container = styled('div')({
  flexGrow: 1,
  position: 'relative',
  background: mapBackground,
  width: '100%',
});

const DrawButton = styled(Button)({
  textTransform: 'none',
});

// Same marker TaDa uses
const markerSvg =
  '<svg height="41" width="27" version="1.1" xmlns="http://www.w3.org/2000/svg"><g fill-rule="nonzero"><g transform="translate(3.0, 29.0)" fill="#000000"><ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="10.5" ry="5.25002273"></ellipse><ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="10.5" ry="5.25002273"></ellipse><ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="9.5" ry="4.77275007"></ellipse><ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="8.5" ry="4.29549936"></ellipse><ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="7.5" ry="3.81822308"></ellipse><ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="6.5" ry="3.34094679"></ellipse><ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="5.5" ry="2.86367051"></ellipse><ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="4.5" ry="2.38636864"></ellipse></g><g fill="#3FB1CE"><path d="M27,13.5 C27,19.074644 20.250001,27.000002 14.75,34.500002 C14.016665,35.500004 12.983335,35.500004 12.25,34.500002 C6.7499993,27.000002 0,19.222562 0,13.5 C0,6.0441559 6.0441559,0 13.5,0 C20.955844,0 27,6.0441559 27,13.5 Z"></path></g><g opacity="0.25" fill="#000000"><path d="M13.5,0 C6.0441559,0 0,6.0441559 0,13.5 C0,19.222562 6.7499993,27 12.25,34.5 C13,35.522727 14.016664,35.500004 14.75,34.5 C20.250001,27 27,19.074644 27,13.5 C27,6.0441559 20.955844,0 13.5,0 Z M13.5,1 C20.415404,1 26,6.584596 26,13.5 C26,15.898657 24.495584,19.181431 22.220703,22.738281 C19.945823,26.295132 16.705119,30.142167 13.943359,33.908203 C13.743445,34.180814 13.612715,34.322738 13.5,34.441406 C13.387285,34.322738 13.256555,34.180814 13.056641,33.908203 C10.284481,30.127985 7.4148684,26.314159 5.015625,22.773438 C2.6163816,19.232715 1,15.953538 1,13.5 C1,6.584596 6.584596,1 13.5,1 Z"></path></g><g transform="translate(6.0, 7.0)" fill="#FFFFFF"></g><g transform="translate(8.0, 8.0)"><circle fill="#000000" opacity="0.25" cx="5.5" cy="5.5" r="5.4999962"></circle><circle fill="#FFFFFF" cx="5.5" cy="5.5" r="5.4999962"></circle></g></g></svg>';

const LocationPinStyle = new Style({
  image: new Icon({
    anchor: [0.5, 1],
    anchorXUnits: 'fraction',
    anchorYUnits: 'fraction',
    opacity: 1,
    src: 'data:image/svg+xml;base64,' + window.btoa(markerSvg),
  }),
});

/**
 * @type {Object<string, import('ol/style/Circle').default>}
 */
const imageStyleCache = {};

const resultStyle = new Style({
  stroke: new Stroke({
    color: 'white',
    width: 2,
  }),
  fill,
});

const CollectionMap = forwardRef((props, ref) => {
  const {
    changeDetection,
    subscription,
    onSelectResult,
    filterValues,
    defaultFilterValues,
    handleFilterChange,
    onMoveEnd = () => {},
    observations,
    resultInfo,
    view,
    updateView,
  } = props;

  const auth = useAuth();
  const mapRef = useRef();
  const draw = useRef();
  const helpOverlay = useRef();
  const layerRef = useRef();
  const heatmapLayer = useRef();
  const mousePositionCtl = useRef();

  const onZoomClick = useZoomCallback(view, updateView);

  const onMapHomeClick = useCallback(() => {
    const extent = getExtentForGeometry(subscription.geometry);
    scaleExtent(extent, 1.25);
    updateView({extent});
  }, [subscription, updateView]);

  const [hovering, updateHovering] = useState(false);
  const [mode, setMode] = useState(MODE.DETECTIONS);
  const [timeMode, setTimeMode] = useState(TIME_MODE.SELECTED);
  const [modalOpen, setModalOpen] = useState(false);
  const [bounds, setBounds] = useState(null);
  const [gradientIndex, setGradientIndex] = useState(0);
  const [disabled, setDisabled] = useState(true);
  const [extent, setExtent] = useState(null);
  const [locationPoint, setLocationPoint] = useState(null);

  const selectedStyle = useMemo(() => getDrawStyle('#001F23'), []);

  const onCloseModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const onPointerMove = useCallback(
    event => {
      if (mode === MODE.DETECTIONS) {
        const feature = getFeatureFromEvent(event);
        updateHovering(!!feature);
      }
      draw.current?.getActive() &&
        helpOverlay.current?.setPosition(event.coordinate);
    },
    [mode, updateHovering],
  );

  const popupCallback = useCallback((event, feature, extent) => {
    if (!event || (!feature && !extent)) {
      setExtent(null);
      setLocationPoint(null);
    }

    if (feature) {
      setLocationPoint(null);
      setExtent(buffer(extent, getWidth(extent) * 0.15));
    } else if (!feature && extent) {
      setExtent(null);
      setLocationPoint(getCenter(extent));
    }
  }, []);

  const [onMapClick] = usePopup(
    popupCallback,
    true,
    onSelectResult,
    resultInfo,
  );

  const handleMoveEnd = useCallback(event => onMoveEnd(event), [onMoveEnd]);

  const confidenceFilter = filterValues.confidence;
  const classFilter = filterValues.classes;

  const registerLoadEvents = useCallback(() => {
    mapRef.current.once('rendercomplete', () => {
      const features = layerRef.current.getFeaturesInExtent(
        transformExtent(
          getExtentForGeometry(props.subscription.geometry),
          'EPSG:4326',
          'EPSG:3857',
        ),
      );
      const newFeatures = [];
      features.forEach(f => {
        const score = f.get('score') || 1;
        // Filter by score
        if (score < confidenceFilter[0] || score > confidenceFilter[1]) {
          return;
        }
        // Filter by class if provided
        if (classFilter && classFilter.length > 0) {
          const outputClass = f.get('object_class_label');
          if (!classFilter.includes(outputClass)) {
            return;
          }
        }
        const newFeature = new Feature({
          geometry: new Point(getCenter(f.getExtent())),
        });
        newFeatures.push(newFeature);
      });
      if (!heatmapLayer.current) {
        const source = new VectorSource({features: newFeatures});
        heatmapLayer.current = new HeatMapLayer({
          gradient: heatMapGradient[gradientIndex],
          source,
          visible: false,
          zIndex: 1,
        });
        mapRef.current.addLayer(heatmapLayer.current);
      } else {
        heatmapLayer.current.getSource().clear();
        heatmapLayer.current.getSource().addFeatures(newFeatures);
      }
    });
  }, [
    confidenceFilter,
    gradientIndex,
    props.subscription.geometry,
    classFilter,
  ]);
  // include classFilter in dependencies so heatmap reloads when selection changes

  useEffect(() => {
    if (layerRef.current) {
      registerLoadEvents();
    }
  }, [props.layer, registerLoadEvents, timeMode, view]);

  useEffect(() => {
    if (heatmapLayer.current) {
      heatmapLayer.current.setVisible(mode === MODE.HEATMAP);
    }
  }, [mode]);

  useEffect(() => {
    mapRef.current.on('pointermove', onPointerMove);
    mapRef.current.on('click', onMapClick);
    mapRef.current.on('moveend', handleMoveEnd);
    mapRef.current.once('rendercomplete', () => {
      setDisabled(false);
    });
    return () => {
      if (mapRef.current) {
        mapRef.current.un('pointermove', onPointerMove);
        mapRef.current.un('click', onMapClick);
        mapRef.current.un('moveend', handleMoveEnd);
      }
    };
  }, [onPointerMove, onMapClick, handleMoveEnd]);

  useEffect(() => {
    mousePositionCtl.current = new MousePosition({
      coordinateFormat: formatCoords,
      projection: 'EPSG:4326',
      className: 'pl-mouse-position',
      target: 'mousecoords',
      undefinedHTML: '',
    });

    mapRef.current.addControl(mousePositionCtl.current);
  }, []);

  const showChangeViewer = useCallback(() => {
    setBounds(transformExtent(view.extent, 'EPSG:4326', 'EPSG:3857'));
    setModalOpen(true);
  }, [view.extent]);

  const clipGeometry = useMercatorGeometry(props.subscription.geometry);
  const [onPrecompose, onPostcompose] = useClipCallbacks(
    clipGeometry,
    clipStyle,
  );

  useImperativeHandle(ref, () => ({
    zoomToExtent(extent) {
      updateView({extent});
    },
    getViewExtent() {
      return view.extent;
    },
    getViewProjection() {
      return mapRef.current.getView().getProjection();
    },
    resizeMap() {
      return mapRef.current.updateSize();
    },
  }));

  let detectionsURL = '';
  let imageryURL = '';
  if (props.layer && observations.length > 0) {
    if (timeMode === TIME_MODE.SELECTED) {
      detectionsURL = getVectorTilesURL(
        props.subscription.id,
        {layer: props.layer},
        auth.user?.accessToken,
      );
    } else if (timeMode === TIME_MODE.ALL) {
      const endDate = new Date(observations[0].observed);
      // add a small buffer because the vector tiles end time is exclusive
      endDate.setSeconds(endDate.getSeconds() + 1);
      const range = `${
        observations[observations.length - 1].observed
      }/${endDate.toISOString()}`;
      detectionsURL = getVectorTilesURL(
        props.subscription.id,
        {datetime: range},
        auth.user?.accessToken,
      );
    }
    imageryURL = getImageryURL(
      props.changeDetection && props.visualLayer
        ? props.visualLayer
        : props.layer,
      auth.user?.accessToken,
    );
  }

  const classColorLookup = getClassColorLookup(props.feed);

  const featureStyle = useCallback(
    feature => {
      const score = feature.get('score') || 1;
      if (score < confidenceFilter[0] || score > confidenceFilter[1]) {
        return null;
      }

      // Filter by class selection
      if (classFilter && classFilter.length > 0) {
        const outputClass = feature.get('object_class_label');
        if (!classFilter.includes(outputClass)) {
          return null;
        }
      }

      let color;
      if (classColorLookup) {
        const outputClass = feature.get('object_class_label');
        const nominalColor = classColorLookup[outputClass];
        if (nominalColor) {
          color = chroma(nominalColor).set('hsl.s', score).hex();
        }
      }
      if (!color) {
        color = scale(score).hex();
      }

      resultStyle.getStroke().setColor(color);
      let imageStyle = imageStyleCache[color];
      if (!imageStyle) {
        imageStyle = new Circle({
          radius: 2,
          fill: new Fill({color}),
        });
        imageStyleCache[color] = imageStyle;
      }
      resultStyle.setImage(imageStyle);
      return resultStyle;
    },
    [classColorLookup, confidenceFilter, classFilter],
  );

  const onToggleChange = useCallback(val => {
    if (val !== null) {
      setMode(val);
    }
  }, []);

  const onTimeModeChange = useCallback((evt, val) => {
    setTimeMode(val ? TIME_MODE.ALL : TIME_MODE.SELECTED);
  }, []);

  const onGradientChange = useCallback(idx => {
    setGradientIndex(idx);
    heatmapLayer.current?.setGradient(heatMapGradient[idx]);
  }, []);

  const enableFilter = observations?.length > 0;

  return (
    <RootDiv>
      <MapToolbar
        disabled={disabled}
        mode={mode}
        onModeChange={onToggleChange}
        timeMode={timeMode}
        onTimeModeChange={onTimeModeChange}
        filterValues={filterValues}
        defaultFilterValues={defaultFilterValues}
        filterDisabled={!enableFilter}
        handleFilterChange={handleFilterChange}
        gradient={heatMapGradient[gradientIndex]}
        onGradientChange={onGradientChange}
        classes={props.feed.outputClasses}
      >
        {changeDetection && mode === MODE.DETECTIONS && (
          <DrawButton
            color="primary"
            disabled={timeMode === TIME_MODE.ALL}
            onClick={showChangeViewer}
            startIcon={
              <SvgIcon>
                <ViewChangeIcon />
              </SvgIcon>
            }
            variant="contained"
          >
            View over time
          </DrawButton>
        )}
      </MapToolbar>
      <Container>
        <Map
          style={{
            cursor: hovering ? 'pointer' : 'default',
            width: '100%',
            height: '100%',
            position: 'absolute',
          }}
          animation={{duration: 300}}
          view={view}
          onViewChange={updateView}
          innerRef={node => (mapRef.current = node)}
        >
          <TileLayer url={MAPBOX_DARK} />
          <TileLayer
            onPreRender={onPrecompose}
            onPostRender={onPostcompose}
            url={imageryURL}
          />
          <VectorLayer
            data={props.subscription.geometry}
            layerStyle={selectStyle}
          />
          {detectionsURL && (
            <VectorTileLayer
              key={detectionsURL}
              url={detectionsURL}
              layerStyle={featureStyle}
              innerRef={node => (layerRef.current = node)}
              opacity={mode === MODE.HEATMAP ? 0 : 100}
            />
          )}
          {!!extent && (
            <VectorLayer
              layerStyle={selectedStyle}
              data={{
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: fromExtent(extent).getCoordinates(),
                },
              }}
            />
          )}
          {!!locationPoint && (
            <VectorLayer
              layerStyle={LocationPinStyle}
              data={{
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: locationPoint,
                },
              }}
            />
          )}
        </Map>
        <MapTools onHomeClick={onMapHomeClick} onZoomClick={onZoomClick} />
        {!!bounds && (
          <ChangeViewer
            bounds={bounds}
            detectionsURL={detectionsURL}
            featureStyle={featureStyle}
            imageryURL={imageryURL}
            layer={props.layer}
            observations={observations}
            onClose={onCloseModal}
            onSelectResult={onSelectResult}
            open={modalOpen}
            subscription={props.subscription}
            resultInfo={resultInfo}
            mosaicsInfo={props.mosaicsInfo}
          />
        )}
      </Container>
      <Box
        width="100%"
        sx={{
          height: theme => theme.spacing(3),
          backgroundColor: 'grey.900',
          color: theme => theme.palette.common.white,
          paddingX: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <Typography id="mousecoords" variant="body2" />
      </Box>
    </RootDiv>
  );
});

CollectionMap.propTypes = {
  changeDetection: PropTypes.bool,
  layer: PropTypes.string,
  visualLayer: PropTypes.string,
  observations: PropTypes.array,
  subscription: PropTypes.object.isRequired,
  feed: PropTypes.object.isRequired,
  onSelectResult: PropTypes.func.isRequired,
  onMoveEnd: PropTypes.func,
  filterValues: PropTypes.object,
  defaultFilterValues: PropTypes.object,
  handleFilterChange: PropTypes.func,
  resultInfo: PropTypes.object,
  view: PropTypes.object,
  updateView: PropTypes.func,
  mosaicsInfo: PropTypes.array,
};

CollectionMap.displayName = 'CollectionMap';

export default memo(CollectionMap);
